import React, { useState, useMemo, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { useClub } from '../context/ClubContext';
import { formatSportDisplayName } from '../utils/sportName';
import { getCourtDisplayNameFromCourt } from '../utils/courtDisplay';

const LightCreditsModal = ({ isOpen, onClose, user, courtData, onSubmit, initialAmount = '' }) => {
  const { getCategoryStyle } = useClub();
  const [purchaseAmount, setPurchaseAmount] = useState(initialAmount);
  const [errors, setErrors] = useState({});

  // Helper function to format currency
  const formatCurrency = (value) => {
    const numericValue = Number(value || 0);
    try {
      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        minimumFractionDigits: 2
      }).format(numericValue);
    } catch {
      return `R${numericValue.toFixed(2)}`;
    }
  };

  // Get all sport permissions and their rates per minute
  const lightsRates = useMemo(() => {
    if (!user?.permissions) return [];
    
    const rates = [];
    for (const [sportKey, sportPermissions] of Object.entries(user.permissions)) {
      if (sportPermissions?.canOperateLights && sportPermissions?.lightsFeePerMinute) {
        const rate = Number(sportPermissions.lightsFeePerMinute) || 0;
        if (rate > 0) {
          const sportName = formatSportDisplayName(sportKey);
          rates.push({ sport: sportName, rate });
        }
      }
    }
    return rates;
  }, [user?.permissions]);

  // Check if all rates are the same
  const allRatesSame = useMemo(() => {
    if (lightsRates.length <= 1) return true;
    const firstRate = lightsRates[0].rate;
    return lightsRates.every(r => r.rate === firstRate);
  }, [lightsRates]);

  // Get courts that support light operations (have floodlights and switches)
  // Only for sports where user has permission to operate lights
  // Grouped by sport and sorted by court number
  const courtsBySport = useMemo(() => {
    if (!courtData || !Array.isArray(courtData) || !user?.permissions) return {};
    
    // Get sports where user has permission to operate lights
    const allowedSports = new Set();
    for (const [sportKey, sportPermissions] of Object.entries(user.permissions)) {
      if (sportPermissions?.canOperateLights) {
        allowedSports.add(sportKey);
      }
    }
    
    const courtsWithLights = courtData.filter(court => {
      const sport = court.court.split('-')[0];
      // Only include courts for sports where user has permission
      if (!allowedSports.has(sport)) return false;
      
      const hasFloodlights = Boolean(court.floodlights);
      const hasSwitches = Boolean(court.switches && court.switches.length > 0);
      return hasFloodlights && hasSwitches;
    });

    // Group by sport
    const grouped = courtsWithLights.reduce((acc, court) => {
      const sport = court.court.split('-')[0];
      if (!acc[sport]) {
        acc[sport] = [];
      }
      acc[sport].push(court);
      return acc;
    }, {});

    // Sort each sport's courts by court number
    Object.keys(grouped).forEach(sport => {
      grouped[sport].sort((a, b) => {
        const aNum = parseInt(a.court.split('-')[1], 10);
        const bNum = parseInt(b.court.split('-')[1], 10);
        return aNum - bNum;
      });
    });

    return grouped;
  }, [courtData, user?.permissions]);

  const sportTypes = Object.keys(courtsBySport);

  const currentCredits = Number(user?.credits ?? 0) || 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate purchase amount
    const amount = parseFloat(purchaseAmount);
    if (!purchaseAmount || isNaN(amount) || amount <= 0) {
      setErrors({ purchaseAmount: 'Please enter a valid amount greater than 0' });
      return;
    }

    // Clear errors
    setErrors({});

    // Call onSubmit with the purchase amount and close modal
    if (onSubmit) {
      onSubmit(amount);
      handleClose();
    }
  };

  const handleClose = () => {
    setPurchaseAmount(initialAmount || '');
    setErrors({});
    onClose();
  };

  // Update purchaseAmount when initialAmount changes (e.g., after payment)
  useEffect(() => {
    if (isOpen && initialAmount) {
      setPurchaseAmount(initialAmount);
    }
  }, [isOpen, initialAmount]);

  return (
    <Dialog open={isOpen} onClose={handleClose} className="fixed z-[60] inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
        <Dialog.Panel className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-slate-600 hover:text-slate-600 rounded-full p-1 hover:bg-slate-100 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <Dialog.Title className="text-xl font-semibold text-slate-800 mb-6">
            Purchase Credits
          </Dialog.Title>

          <div className="space-y-6">
            {/* Current Credits */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="text-sm text-slate-600 mb-1">Current Credits</div>
              <div className="text-2xl font-semibold text-slate-800">
                {formatCurrency(currentCredits)}
              </div>
            </div>

            <p className="text-sm text-slate-600">
              Credits can be used for bookings, optional extras, and Smart Light operations where supported.
              If you cancel a paid booking before the cutoff, the fee is credited back for future use.
            </p>

            {/* Rate per Minute */}
            {sportTypes.length > 0 && lightsRates.length > 0 && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="text-sm text-blue-800 mb-1">Lights Rate per Minute</div>
                <div className={`font-semibold text-blue-800 ${allRatesSame ? 'text-lg' : 'text-sm'}`}>
                  {allRatesSame ? (
                    `${formatCurrency(lightsRates[0].rate)}/min`
                  ) : lightsRates.length === 2 ? (
                    <div className="flex justify-between">
                      <span>{lightsRates[0].sport} {formatCurrency(lightsRates[0].rate)}/min</span>
                      <span>{lightsRates[1].sport} {formatCurrency(lightsRates[1].rate)}/min</span>
                    </div>
                  ) : (
                    <div className="flex justify-around">
                      {lightsRates.map(({ sport, rate }) => (
                        <span key={sport}>
                          {sport} {formatCurrency(rate)}/min
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Courts with Lights */}
            {sportTypes.length > 0 ? (
              <div>
                <div className="text-sm font-medium text-slate-600 mb-2">
                  Courts With Smart Light Operations
                </div>
                <div className="flex flex-col gap-2">
                  {sportTypes.map((sport) => {
                    const sportName = formatSportDisplayName(sport);
                    return (
                      <div key={sport} className="flex justify-around items-center gap-1">
                        {courtsBySport[sport].map(court => {
                          const aliasLabel = getCourtDisplayNameFromCourt(court, '').trim();
                          const displayLabel = aliasLabel || `${sportName} ${court.court.split('-')[1]}`;
                          return (
                            <span
                              key={court.court}
                              className={`flex-grow mx-1 px-2 py-1 text-sm rounded border text-center ${getCategoryStyle({ 
                                category: 'Available', 
                                court: court.court 
                              })}`}
                              style={{ flexBasis: '0', minWidth: 0 }}
                            >
                              {displayLabel}
                            </span>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-600">
                This club does not have any Smart Lights configured.
              </div>
            )}

            {/* Description */}
            {sportTypes.length > 0 && (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="text-sm font-medium text-slate-600 mb-2">How Credits Work for Smart Lights</div>
                <p className="text-sm text-slate-600">
                  Use credits to control Smart Lights during your booking. If a court has working smart switches controlling the floodlights, 
                  you can turn them on and off from the booking details during your booking timeCredits are deducted
                  when lights are turned on and refunded if you turn them off before the booking ends
                  (based on the time remaining in your booking).
                </p>
              </div>
            )}

            {/* Purchase Amount Input */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Amount to Purchase (ZAR)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-600">
                    R
                  </span>
                  <input
                    type="number"
                    value={purchaseAmount}
                    onChange={(e) => {
                      setPurchaseAmount(e.target.value);
                      if (errors.purchaseAmount) {
                        setErrors({});
                      }
                    }}
                    step="0.01"
                    min="0.01"
                    className={`block w-full pl-8 pr-4 py-2 text-slate-600 border ${
                      errors.purchaseAmount ? 'border-red-500' : 'border-slate-300'
                    } rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition`}
                    placeholder="0.00"
                  />
                </div>
                {errors.purchaseAmount && (
                  <p className="mt-1 text-xs text-red-500">{errors.purchaseAmount}</p>
                )}
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-1 border-slate-200">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default LightCreditsModal;

